<script>
import rowComponent from '../rowComponent'
import speciesOfInterestOnline from './SpeciesOfInterest'
import speciesOfInterestPdf from './SpeciesOfInterestPdf'
import commonFunctions from '../../mixins/commonFunctions'
export default {
  name: 'RowComponentWrapper',
  components: { rowComponent, speciesOfInterestOnline, speciesOfInterestPdf },
  mixins: [commonFunctions],
  props: {
    rowComponentData: {
      type: Array,
      default: () => []
    },
    healthAssociation: {
      type: Object,
      default: () => ({
        goodHealth: true,
        poorHealth: true,
        noHealth: true
      })
    },
    isPdf: {
      type: Boolean,
      default: false
    },
    alwaysDisplayGlossary: {
      type: Boolean,
      default: false
    },
    displayGlossaryIfHealthAssociation: {
      type: Boolean,
      default: false
    },
    category: {
      type: String,
      default: ''
    },
    containsMultipleSamples: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    detectedColor () {
      return (
          this.reportConfiguration?.colors?.speciesOfInterest?.detected ||
          '#00000'
      )
    },
    notDetectedColor () {
      return (
          this.reportConfiguration?.colors?.speciesOfInterest?.notDetected ||
          '#000000'
      )
    },
    filteredGenusData () {
      const rowComponentData = this.rowComponentData
      const filteredGenusData = rowComponentData?.map((genusData) => {
          const genus = { ...genusData }
          const filteredSpeciesOnHealthAssociation = this.filterSpeciesOnHealthAssociation(
            this.allGroupItemChildren(genus)
          )

          genus.speciesRelatedToCurrentGenus =
            genus.speciesRelatedToCurrentGenus?.filter((species) =>
              filteredSpeciesOnHealthAssociation.find(
                (filteredSpecies) =>
                  filteredSpecies.sampleSpeciesUUID ===
                  species.sampleSpeciesUUID
              )
            ) || []
          genus.speciesRelatedToCurrentGenusGroup =
            genus.speciesRelatedToCurrentGenusGroup?.filter((species) =>
              filteredSpeciesOnHealthAssociation.find(
                (filteredSpecies) =>
                  filteredSpecies.sampleSpeciesUUID ===
                  species.sampleSpeciesUUID
              )
            ) || []
          genus.speciesRelatedToCurrentSpeciesGroup =
            genus.speciesRelatedToCurrentSpeciesGroup?.filter((species) =>
              filteredSpeciesOnHealthAssociation.find(
                (filteredSpecies) =>
                  filteredSpecies.sampleSpeciesUUID ===
                  species.sampleSpeciesUUID
              )
            ) || []
          return genus
        })
      return filteredGenusData
    }
  },
  methods: {
    allGroupItemChildren (genus) {
      return [
        ...(genus?.speciesRelatedToCurrentGenus || []),
        ...(genus?.speciesRelatedToCurrentGenusGroup || []),
        ...(genus?.speciesRelatedToCurrentSpeciesGroup || [])
      ]
    },
    isSpeciesDetected (genus, isPrimarySample = false) {
      if (!this.allGroupItemChildren(genus)) return false
      const allSpecies = this.allGroupItemChildren(genus)
      return allSpecies.findIndex((species) => {
        return (
          (isPrimarySample && species.abundanceValue > 0) ||
          (!isPrimarySample && species.abundanceValueSecondarySample > 0)
        )
      }) !== -1
    },
    filterSpeciesOnHealthAssociation (species) {
      if (
        this.healthAssociation.goodHealth &&
        this.healthAssociation.poorHealth &&
        this.healthAssociation.noHealth
      ) {
        return species
      }

      let speciesUUIDAssociatedToGoodHealth = []
      let speciesUUIDAssociatedToPoorHealth = []
      let speciesUUIDAssociatedToNoHealth = []

      if (this.healthAssociation.goodHealth) {
        speciesUUIDAssociatedToGoodHealth = species.filter(
          (specie) => specie.isRelatedToBetterHealth === true
        )
      }
      if (this.healthAssociation.poorHealth) {
        speciesUUIDAssociatedToPoorHealth = species.filter(
          (specie) => specie.isSpecieRelatedToDisease === true
        )
      }
      if (this.healthAssociation.noHealth) {
        speciesUUIDAssociatedToNoHealth = species.filter(
          (specie) =>
            specie.isRelatedToBetterHealth !== true &&
            specie.isSpecieRelatedToDisease !== true
        )
      }

      return [
        ...speciesUUIDAssociatedToGoodHealth,
        ...speciesUUIDAssociatedToPoorHealth,
        ...speciesUUIDAssociatedToNoHealth
      ]
    },
    displayGlossary (specie) {
      if (this.alwaysDisplayGlossary) return true
      const { isRelatedToBetterHealth, isSpecieRelatedToDisease } = specie
      return this.displayGlossaryIfHealthAssociation && (isRelatedToBetterHealth || isSpecieRelatedToDisease)
    },
    hasChildrenItems (genusData) {
      return Boolean(
          genusData?.speciesRelatedToCurrentGenus?.length ||
            genusData?.speciesRelatedToCurrentGenusGroup?.length ||
            genusData?.speciesRelatedToCurrentSpeciesGroup.length
      )
    },
    displayInfoHeader (genusData) {
      return !this.isPdf && this.hasChildrenItems(genusData)
    },
    getLanguageSpecificLabels (genusData) {
      const labelText = genusData.labelKey
      let languageKey = `details.sections.interest.category.${this.category}.genera.${genusData.labelKey}`
      languageKey = languageKey.toLowerCase()
      const labelTextFromLanguageFile = this.i18n.t(languageKey)
      if (!languageKey) return labelText
      if (languageKey === labelTextFromLanguageFile) return labelText

      return labelTextFromLanguageFile
    }
  }
}
</script>
<template>
  <div>
    <div
      v-for="genusData in filteredGenusData"
      :key="genusData.uuid"
    >
      <rowComponent
        class="col-12 mx-auto row-component graceful-page-break"
        :detail-row-visible="genusData.visible"
        @toggleDescriptionRow="
          $emit('toggleDescriptionRow', { visible: $event, rowInfo: genusData })
        "
      >
        <template v-slot:titleRow>
          <div
            v-if="containsMultipleSamples"
            class="row label"
          >
            <div class="col-6 label-genus">
              {{ getLanguageSpecificLabels(genusData) }}
            </div>
            <div class="col-6 label-detection row">
              <div class="col-6 p-0 text-left">
                <span
                  class="badge badge-pill sample-badge__primary sample-badge-label__primary px-4 mr-2"
                >
                  {{ i18n.t('badge.a') }}
                </span>
                <span
                  :style="{
                    color: isSpeciesDetected(genusData, true)
                      ? detectedColor
                      : notDetectedColor
                  }"
                >
                  {{
                    isSpeciesDetected(genusData, true)
                      ? i18n.t(
                        'details.sections.interest.rowComponent.detected'
                      )
                      : i18n.t(
                        'details.sections.interest.rowComponent.notDetected'
                      )
                  }}
                </span>
              </div>
              <div class="col-6 p-0 d-flex justify-content-between">
                <div>
                  <span
                    class="badge badge-pill sample-badge__secondary sample-badge-label__secondary px-4 mr-2"
                  >
                    {{ i18n.t('badge.b') }}
                  </span>
                  <span
                    :style="{
                      color: isSpeciesDetected(genusData)
                        ? detectedColor
                        : notDetectedColor
                    }"
                  >
                    {{
                      isSpeciesDetected(genusData)
                        ? i18n.t(
                          'details.sections.interest.rowComponent.detected'
                        )
                        : i18n.t(
                          'details.sections.interest.rowComponent.notDetected'
                        )
                    }}
                  </span>
                </div>
                <div
                  v-if="!isPdf"
                  class="expandCollapseChevron"
                >
                  <i
                    :class="{
                      'fa fa-chevron-up':
                        hasChildrenItems(genusData) && genusData.visible,
                      'fa fa-chevron-down':
                        !hasChildrenItems(genusData) ||
                        (hasChildrenItems(genusData) && !genusData.visible),
                      detected: hasChildrenItems(genusData),
                      notDetected: !hasChildrenItems(genusData)
                    }"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            v-else
            class="row label"
          >
            <div class="col-6 label-genus">
              {{ getLanguageSpecificLabels(genusData) }}
            </div>
            <div
              class="col-6 label-detection my-auto"
              :style="{
                color: hasChildrenItems(genusData)
                  ? detectedColor
                  : notDetectedColor
              }"
            >
              {{
                hasChildrenItems(genusData)
                  ? i18n.t('details.sections.interest.rowComponent.detected')
                  : i18n.t('details.sections.interest.rowComponent.notDetected')
              }}
              <div
                v-if="!isPdf"
                class="expandCollapseChevron d-inline-block"
              >
                <i
                  :class="{
                    'fa fa-chevron-up':
                      hasChildrenItems(genusData) && genusData.visible,
                    'fa fa-chevron-down':
                      !hasChildrenItems(genusData) ||
                      (hasChildrenItems(genusData) && !genusData.visible),
                    detected: hasChildrenItems(genusData),
                    notDetected: !hasChildrenItems(genusData)
                  }"
                />
              </div>
            </div>
          </div>
        </template>
        <template v-slot:descriptionRow>
          <div
            v-if="displayInfoHeader(genusData)"
            class="row col-12 info-header"
          >
            <span class="col-3 text-center">{{
              i18n.t('general.species')
            }}</span>
            <span class="col-3 text-center">
              {{ i18n.t('details.sections.interest.rowComponent.moreInfo') }}
            </span>
            <span
              class="text-center"
              :class="containsMultipleSamples ? 'col-6' : 'col-4'"
            >
              {{ i18n.t('general.relativeAbundance') }}
            </span>
            <span
              v-if="!containsMultipleSamples"
              class="col-2 text-center"
            >
              {{ i18n.t('general.level') }}
            </span>
          </div>
          <div class="speciesOfInterest-component">
            <div
              v-for="(specie, specieIndex) in allGroupItemChildren(genusData)"
              :key="`specie-in-${genusData.uuid}-${specieIndex}`"
              class="col-12"
              :class="isPdf ? 'component-pdf' : 'component-online'"
            >
              <div v-if="isPdf">
                <speciesOfInterestPdf
                  v-if="isPdf"
                  :species-data="specie"
                  :category="category"
                />
                <div
                  v-if="displayGlossary(specie)"
                  class="section-card-body"
                >
                  <div
                    class="mt-3 terminology-comparison"
                    v-html="getItemLevelDescription(specie, 'species')"
                  />
                  <div
                    class="mt-3 terminology-description"
                    v-html="specie.description"
                  />
                </div>
              </div>
              <speciesOfInterestOnline
                v-if="!isPdf && genusData.visible"
                class="component-online"
                :category="category"
                :species-data="specie"
                :contains-multiple-samples="containsMultipleSamples"
                @display:speciesDetails="
                  $emit('display:speciesDetails', $event)
                "
              />
            </div>
          </div>
        </template>
      </rowComponent>
    </div>
  </div>
</template>
